/**
 * Terrain shader: the printed base map. Everything is computed per pixel from the smooth baked
 * fields (see bake/terrainFields.ts), so it is crisp at every zoom level:
 *   paper sheet + drop shadow on the table · graduated neatline · organic coastline with ink rule ·
 *   engraved water-lining and deep-water gradient · terrain colours blended softly · hillshade ·
 *   ink contour lines (fwidth anti-aliased) · stroke-splatted hachures on hills and mountains ·
 *   marsh reed symbols · forest stipple · paper fibre grain.
 */

import { FRAG_HEADER, VERT_HEADER } from '../gfx/gl';
import { PAL } from '../palette';
import { glslColor as c, GLSL_COAST, GLSL_COMMON } from './common';

export const WORLD_QUAD_VERT = /* glsl */ `${VERT_HEADER}
in vec2 aPosition;
out vec2 vWorld;
void main() {
  vWorld = aPosition;
  gl_Position = toClip(aPosition);
}
`;

export const TERRAIN_FRAG = /* glsl */ `${FRAG_HEADER}
in vec2 vWorld;
uniform sampler2D uFieldA;
uniform sampler2D uFieldB;
uniform sampler2D uFieldC;
uniform sampler2D uNoise;
uniform sampler2D uPaper;
uniform vec4 uBake;      // origin.xy, 1 / extent.xy
uniform vec2 uBakeSize;  // texels
uniform vec4 uNeat;
uniform vec4 uFrame;
uniform vec4 uSheet;
uniform float uZoom;     // device px per world unit
uniform float uZoomCss;  // css px per world unit
uniform float uSea;      // sea-level elevation 0..1

${GLSL_COMMON}
${GLSL_COAST}

const vec3 PAPER = ${c(PAL.paper)};
const vec3 PAPER_AGED = ${c(0xd9c9a3)};
const vec3 PLAINS = ${c(0xd8cfa9)};
const vec3 DRY = ${c(0xdccb98)};
const vec3 WET = ${c(0xc9cda0)};
const vec3 HIGH = ${c(0xcdbb92)};
const vec3 FOREST = ${c(0xa9b389)};
const vec3 FOREST_DOT = ${c(0x6f7d55)};
const vec3 MOUNT = ${c(0xbfae8d)};
const vec3 SNOW = ${c(0xefe8d8)};
const vec3 MARSH = ${c(0xb9bf9a)};
const vec3 REED = ${c(0x7f8a66)};
const vec3 URBAN = ${c(0xc9b9a0)};
const vec3 SHALLOW = ${c(0xa9c1c4)};
const vec3 DEEP1 = ${c(0x86a6b3)};
const vec3 DEEP2 = ${c(0x6f93a3)};
const vec3 LAKE = ${c(0x9fbcc2)};
const vec3 WAVE_INK = ${c(0x3f6a80)};
const vec3 COAST_INK = ${c(0x3b4a4c)};
const vec3 CONTOUR = ${c(PAL.contour)};
const vec3 HACH_INK = ${c(0x6b5a40)};
const vec3 INK = ${c(PAL.ink)};
const vec3 SHADOW_TINT = ${c(0x7d8290)};
const vec3 LIT_TINT = ${c(0xf4e7c4)};

float rectDist(vec2 p, vec4 r) {
  vec2 c = (r.xy + r.zw) * 0.5;
  vec2 h = (r.zw - r.xy) * 0.5;
  vec2 d = abs(p - c) - h;
  return length(max(d, 0.0)) + min(max(d.x, d.y), 0.0);
}

// Coverage of an anti-aliased stroke of half-width hw (world) at distance dist, px = world/px.
float stroke(float dist, float hw, float px) {
  float w = max(hw, 0.5 * px);
  return clamp((w - dist) / px + 0.5, 0.0, 1.0) * min(1.0, hw / (0.5 * px));
}

// Hachures: short strokes pointing down the fall line, one per jittered cell, heavier on
// steep and shadowed slopes (Lehmann-style engraving).
float hachures(vec2 p, float px) {
  const float CELL = 7.0;
  vec2 cell = floor(p / CELL);
  float cover = 0.0;
  for (int j = -1; j <= 1; j++) {
    for (int i = -1; i <= 1; i++) {
      vec2 cc = cell + vec2(float(i), float(j));
      vec2 hj = hash22(cc);
      vec2 cp = (cc + 0.5 + (hj - 0.5) * 0.3) * CELL;
      vec2 cuv = (cp - uBake.xy) * uBake.zw;
      vec4 a = texture(uFieldA, cuv);
      vec4 b = texture(uFieldB, cuv);
      float relief = max(b.g * 0.8, b.b);
      float slope = length(a.zw);
      // only real slopes get strokes; steeper = longer, heavier, and denser (fewer skipped cells)
      float st = smoothstep(0.05, 0.3, slope);
      if (relief < 0.3 || st <= 0.0 || a.y < 7.0 || hj.x > 0.35 + 0.65 * st) continue;
      vec2 dir = a.zw / slope;
      float halfLen = CELL * (0.18 + 0.32 * st);
      vec2 d = p - cp;
      float t = clamp(dot(d, dir), -halfLen, halfLen);
      float dist = length(d - dir * t);
      float facing = dot(-dir, vec2(-0.6, -0.8));
      float hw = mix(0.16, 0.42, st) * (1.0 - 0.45 * facing);
      cover = max(cover, stroke(dist, hw, px) * smoothstep(0.3, 0.6, relief) * (0.35 + 0.65 * st));
    }
  }
  return cover;
}

// Marsh symbol: a reed dash with three tufts, on staggered rows.
float marshSymbols(vec2 p, float px) {
  const vec2 CELL = vec2(14.0, 8.5);
  float row = floor(p.y / CELL.y);
  float shift = mod(row, 2.0) * 0.5 * CELL.x;
  vec2 c = vec2(floor((p.x + shift) / CELL.x), row);
  vec2 origin = vec2(c.x * CELL.x - shift, c.y * CELL.y);
  vec2 h = hash22(c + 17.0);
  vec2 centre = origin + CELL * 0.5 + (h - 0.5) * vec2(4.0, 2.0);
  vec2 cuv = (centre - uBake.xy) * uBake.zw;
  float w = texture(uFieldB, cuv).a;
  if (w < 0.45 || texture(uFieldA, cuv).y < 5.0) return 0.0;
  vec2 d = p - centre;
  float len = 3.4 + h.x * 1.6;
  float dist = length(vec2(max(abs(d.x) - len, 0.0), d.y));
  for (int k = -1; k <= 1; k++) {
    float x = float(k) * 1.9;
    vec2 e = vec2(d.x - x - float(k) * 0.35 * clamp(-d.y, 0.0, 2.6), d.y - clamp(d.y, -2.6, 0.0));
    dist = min(dist, length(e));
  }
  return stroke(dist, 0.28, px) * smoothstep(0.45, 0.7, w);
}

vec3 terrainColor(vec2 p, float px) {
  vec2 uv = (p - uBake.xy) * uBake.zw;
  vec4 A = textureBicubic(uFieldA, uv, uBakeSize);
  vec4 B = texture(uFieldB, uv);
  vec4 C = texture(uFieldC, uv);
  vec4 nM = texture(uNoise, p * (1.0 / 173.0) + vec2(0.37, 0.11));
  vec4 nS = texture(uNoise, p * (1.0 / 59.0) + vec2(0.71, 0.53));

  float coast = A.y + coastWobble(p);
  float cw = max(fwidth(coast), 1e-3);
  float land = smoothstep(-0.7 * cw, 0.7 * cw, coast);

  // ---- water: shallow → deep, a darker wash along the shore, engraved water-lines
  float depth = max(-coast, 0.0);
  vec3 water = mix(SHALLOW, DEEP1, smoothstep(6.0, 120.0, depth));
  water = mix(water, DEEP2, smoothstep(120.0, 480.0, depth));
  water = mix(water, LAKE, C.b * 0.85);
  water = mix(water, SHALLOW, C.a * (1.0 - C.b) * 0.35 * (1.0 - smoothstep(20.0, 80.0, depth)));
  water = mix(water, DEEP1 * 0.92, (1.0 - smoothstep(0.0, 22.0, depth)) * 0.35);
  water *= 0.975 + 0.05 * (nM.r - 0.5);
  float s = pow(depth / 3.0, 0.74);
  float sw = max(fwidth(s), 1e-4);
  float k = floor(s + 0.5);
  float dl = abs(fract(s + 0.5) - 0.5) / sw;
  float lineHw = clamp(0.32 * uZoomCss, 0.35, 0.8);
  float wave = clamp(lineHw + 0.5 - dl, 0.0, 1.0);
  wave *= step(0.5, k) * (1.0 - smoothstep(4.0, 9.0, k)) * smoothstep(0.4, 0.15, sw);
  water = mix(water, WAVE_INK, wave * 0.4 * (1.0 - k / 11.0));

  // ---- land: moisture & altitude tint, terrain classes with noisy organic edges
  float elev = A.x;
  float h = clamp((elev - uSea) / (1.0 - uSea), 0.0, 1.0);
  // organic class edges: low-frequency noise bends outlines well away from the hex shape
  vec4 nB = texture(uNoise, p * (1.0 / 347.0) + vec2(0.52, 0.27));
  float tn = (nB.r - 0.5) * 0.5 + (nM.g - 0.5) * 0.4 + (nS.b - 0.5) * 0.2;
  float moist = C.r + (nM.a - 0.5) * 0.12;
  vec3 ground = mix(DRY, WET, smoothstep(0.3, 0.72, moist));
  ground = mix(ground, PLAINS, 0.45);
  ground = mix(ground, HIGH, smoothstep(0.4, 0.9, h) * 0.5);
  float forest = smoothstep(0.4, 0.6, B.r + tn);
  float marsh = smoothstep(0.38, 0.62, B.a + tn);
  float mount = smoothstep(0.34, 0.66, B.b + tn * 0.6);
  float urban = smoothstep(0.52, 0.72, C.g + tn * 0.3);
  ground = mix(ground, FOREST, forest * 0.85);
  float stip = texture(uNoise, p * (1.0 / 21.0) + vec2(0.3, 0.8)).a;
  ground = mix(ground, FOREST_DOT, forest * smoothstep(0.64, 0.74, stip) * 0.3);
  ground = mix(ground, MARSH, marsh * 0.8);
  ground = mix(ground, MOUNT, mount * 0.75);
  ground = mix(ground, SNOW, mount * smoothstep(0.8, 0.95, h + tn * 0.12));
  ground = mix(ground, URBAN, urban * 0.4);
  // built-up area: fine ink cross-hatching, like the town blocks of a printed map
  if (urban > 0.01) {
    float cu = (p.x - p.y) * 0.70710678;
    float cv = (p.x + p.y) * 0.70710678;
    float hu = abs(fract(cu / 3.2 + 0.5) - 0.5) * 3.2 / px;
    float hv = abs(fract(cv / 3.2 + 0.5) - 0.5) * 3.2 / px;
    float cross = max(clamp(0.9 - hu, 0.0, 1.0), clamp(0.9 - hv, 0.0, 1.0)) * smoothstep(1.0, 2.5, 3.2 * uZoomCss);
    ground = mix(ground, INK, urban * (0.04 + 0.12 * cross));
  }

  // hillshade: light from the north-west, cool shadows, warm lit slopes
  vec3 nrm = normalize(vec3(-A.zw * 2.6, 1.0));
  vec3 L = normalize(vec3(-0.55, -0.75, 0.9));
  float shade = dot(nrm, L) - L.z;
  ground *= 1.0 + shade * 0.55;
  ground = mix(ground, SHADOW_TINT, clamp(-shade, 0.0, 1.0) * 0.45);
  ground = mix(ground, LIT_TINT, clamp(shade, 0.0, 1.0) * 0.35);

  // contour lines every 12 elevation steps, index contour every 5th
  float cl = (elev - uSea) * (255.0 / 12.0);
  float cfw = max(fwidth(cl), 1e-4);
  float cd = abs(fract(cl + 0.5) - 0.5) / cfw;
  float isIndex = step(mod(floor(cl + 0.5), 5.0), 0.5);
  float cHw = mix(0.42, 0.7, isIndex) * clamp(uZoomCss * 0.9, 0.8, 1.5);
  float contour = clamp(cHw + 0.5 - cd, 0.0, 1.0) * smoothstep(0.6, 0.2, cfw) * step(0.5, cl);
  ground = mix(ground, CONTOUR, contour * mix(0.3, 0.46, isIndex));

  float detail = smoothstep(2.6, 5.0, 7.0 * uZoomCss);
  if (detail > 0.0 && max(B.g, B.b) > 0.04) ground = mix(ground, HACH_INK, hachures(p, px) * 0.5 * detail);
  if (detail > 0.0 && B.a > 0.12) ground = mix(ground, REED, marshSymbols(p, px) * 0.8 * detail);

  vec3 col = mix(water, ground, land);
  float coastHw = clamp(0.5 * uZoomCss, 0.55, 1.3);
  float coastLine = clamp(coastHw + 0.5 - abs(coast) / cw, 0.0, 1.0);
  return mix(col, COAST_INK, coastLine * 0.8);
}

void main() {
  vec2 p = vWorld;
  float px = 1.0 / uZoom;
  vec4 nL = texture(uNoise, p * (1.0 / 1300.0));

  // ---- the sheet on the table
  float dSheet = rectDist(p, uSheet) + (nL.b - 0.5) * 3.0;
  float sheetMask = 1.0 - smoothstep(-0.7 * px, 0.7 * px, dSheet);
  float dShadow = rectDist(p - vec2(9.0, 13.0), uSheet);
  float shadowA = 0.62 * (1.0 - smoothstep(-26.0, 70.0, dShadow));
  if (sheetMask <= 0.0) {
    finalColor = vec4(0.0, 0.0, 0.0, shadowA);
    return;
  }

  // aged paper: browner towards the edges, gentle large-scale mottling
  vec3 col = PAPER;
  col = mix(col, PAPER_AGED, smoothstep(-90.0, 0.0, dSheet) * 0.45 + (nL.r - 0.5) * 0.18);

  float dNeat = rectDist(p, uNeat);
  float dFrame = rectDist(p, uFrame);
  if (dNeat < px) {
    float neatMask = 1.0 - smoothstep(-0.5 * px, 0.5 * px, dNeat);
    col = mix(col, terrainColor(p, px), neatMask);
  }

  // graduated border between the neatline and the outer rule
  if (dNeat > -2.0 * px && dFrame < 3.0 * px) {
    float inner = stroke(abs(dNeat), 0.6, px);
    float outer = stroke(abs(dFrame), 1.4, px);
    float bw = (uFrame.z - uNeat.z);
    float band = step(0.0, dNeat - bw * 0.3) * step(dNeat, bw * 0.72);
    // Blocks follow the hex columns along the top/bottom and the hex rows along the sides.
    vec2 cellIdx = floor(vec2((p.x - HEX_W * 0.5) / HEX_W, (p.y - HEX_SIZE * 0.25) / (HEX_SIZE * 1.5)));
    float ox = max(uNeat.x - p.x, p.x - uNeat.z);
    float oy = max(uNeat.y - p.y, p.y - uNeat.w);
    float idx = oy > ox ? cellIdx.x : cellIdx.y;
    float corner = step(0.0, ox) * step(0.0, oy);
    float block = band * max(mod(idx, 2.0), corner);
    col = mix(col, INK, max(max(inner, outer), block) * 0.85);
  }

  // paper fibre grain over everything printed on the sheet
  float fib = texture(uPaper, p * (1.0 / 430.0)).r;
  col *= 1.0 + (fib - 0.5) * 0.32;
  col *= 0.99 + (nL.g - 0.5) * 0.05;

  finalColor = vec4(col * sheetMask, sheetMask) + vec4(0.0, 0.0, 0.0, shadowA) * (1.0 - sheetMask);
}
`;
